import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, IconButton, TextField, Paper, Grid, Button, Table, TableHead, TableRow, TableCell, TableBody, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getEstimate, updateEstimate } from '../services/estimateService';
import { useNotification } from '../components/layouts/Layout';

const TABS = ['Karta Info','TES','Przedmiar','Kosztorys Szczegółowy','Kosztorys Uproszczony','RMS','Podsumowanie'];

const Estimates2DetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);
  const { showNotification } = useNotification();

  // Formularz – Karta Info
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({
    name: '',
    object_name: '',
    client: '',
    prepared_at: '',
    markup_kz: '',
    markup_kp: '',
    markup_z: '',
    vat: ''
  });

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!id) return;
        const est = await getEstimate(Number(id));
        if (!mounted || !est) return;
        setForm({
          name: est.name || '',
          object_name: (est as any).object_name || '',
          client: est.client || '',
          prepared_at: est.prepared_at || '',
          markup_kz: String(est.markup_kz ?? ''),
          markup_kp: String(est.markup_kp ?? ''),
          markup_z: String(est.markup_z ?? ''),
          vat: String(est.vat ?? '')
        });
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  const handleChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    try {
      if (!id) return;
      const payload: any = {
        name: form.name?.trim(),
        object_name: form.object_name?.trim(),
        client: form.client?.trim(),
        prepared_at: form.prepared_at,
        markup_kz: Number(form.markup_kz || 0),
        markup_kp: Number(form.markup_kp || 0),
        markup_z: Number(form.markup_z || 0),
        vat: Number(form.vat || 0)
      };
      await updateEstimate(Number(id), payload);
      showNotification('Zapisano zmiany kosztorysu', 'success');
    } catch (e: any) {
      showNotification('Nie udało się zapisać zmian', 'error');
    }
  };

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      if (isMac && e.metaKey && e.ctrlKey && !e.altKey && !e.shiftKey) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          setTab((t) => Math.min(t + 1, TABS.length - 1));
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setTab((t) => Math.max(t - 1, 0));
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '60vh', pb: 6 }}>
      <Box sx={{ flex: '1 1 auto' }}>
        <Box sx={{ pt: 4 }}>
          {/* Karta Info */}
          {tab === 0 && (
            <Box>
              <Box sx={{ mb: '25px' }}>
                <span style={{
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 600,
                  fontSize: '32px',
                  lineHeight: '100%',
                  letterSpacing: 0 as any,
                  color: 'rgba(2, 2, 66, 1)'
                }}>Karta informacji</span>
              </Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, borderColor: 'rgba(212,212,217,1)' }}>
                    <Box sx={{
                      fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                      fontWeight: 600,
                      fontSize: '18px',
                      color: 'rgba(2, 2, 66, 1)',
                      mb: 2
                    }}>1. Informacje podstawowe</Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField label="Nazwa kosztorysu" fullWidth size="small" multiline minRows={2} value={form.name} onChange={handleChange('name')} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField label="Nazwa obiektu" fullWidth size="small" multiline minRows={3} maxRows={6} value={form.object_name} onChange={handleChange('object_name')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Zamawiający" fullWidth size="small" multiline minRows={3} maxRows={6} value={form.client} onChange={handleChange('client')} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField type="date" label="Data opracowania" fullWidth size="small" value={form.prepared_at} onChange={handleChange('prepared_at')} InputLabelProps={{ shrink: true }} />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, borderColor: 'rgba(212,212,217,1)' }}>
                    <Box sx={{
                      fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                      fontWeight: 600,
                      fontSize: '18px',
                      color: 'rgba(2, 2, 66, 1)',
                      mb: 2
                    }}>2. Narzuty kosztorysu</Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <TextField label="Kz (%)" fullWidth size="small" value={form.markup_kz} onChange={handleChange('markup_kz')} />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField label="Kp (%)" fullWidth size="small" value={form.markup_kp} onChange={handleChange('markup_kp')} />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField label="Z (%)" fullWidth size="small" value={form.markup_z} onChange={handleChange('markup_z')} />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField label="VAT (%)" fullWidth size="small" value={form.vat} onChange={handleChange('vat')} />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ color: 'text.secondary', fontSize: 12 }}>Pole Rg (roboczogodzina) dodamy przy wprowadzaniu kalkulacji RMS.</Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleSave} sx={{ textTransform: 'none', backgroundColor: 'rgba(2, 2, 66, 1)', '&:hover': { backgroundColor: 'rgba(2, 2, 66, 0.78)' } }}>
                  Zapisz zmiany
                </Button>
              </Box>
            </Box>
          )}

          {/* TES */}
          {tab === 1 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{
                  fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
                  fontWeight: 600,
                  fontSize: '20px',
                  color: 'rgba(2, 2, 66, 1)'
                }}>TES</Box>
                <Tooltip title="Dodaj dział">
                  <IconButton color="primary">
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper variant="outlined" sx={{ borderColor: 'rgba(212,212,217,1)' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nr działu</TableCell>
                      <TableCell>Nazwa działu</TableCell>
                      <TableCell>Pozycje z kosztorysu (od – do)</TableCell>
                      <TableCell align="right">Wartość netto</TableCell>
                      <TableCell align="right">Podatek VAT</TableCell>
                      <TableCell align="right">Wartość brutto</TableCell>
                      <TableCell align="right">Akcje</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Placeholder – wpięcie realnych danych w kolejnym kroku */}
                    <TableRow>
                      <TableCell>1</TableCell>
                      <TableCell>Kosztorys</TableCell>
                      <TableCell>1 – 999999</TableCell>
                      <TableCell align="right">0,00</TableCell>
                      <TableCell align="right">0,00</TableCell>
                      <TableCell align="right">0,00</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Więcej">
                          <IconButton size="small">
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{
        flexShrink: 0,
        borderTop: '1px solid',
        borderColor: 'divider',
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        height: 36,
        zIndex: 1200
      }}>
        <Box sx={{ flex: '0 0 auto', px: 0.5 }}>
          <IconButton aria-label="Powrót" onClick={()=> navigate('/estimates2')} size="small" sx={{ mb: 0.25 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: '1 1 auto' }} />
        <Tabs
          value={tab}
          onChange={(_,v)=>setTab(v)}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            ml: 'auto',
            pr: 0,
            minHeight: 36,
            py: 0,
            '& .MuiTabs-flexContainer': { justifyContent: 'flex-end', alignItems: 'center' },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
              fontWeight: 500,
              fontSize: '14px',
              color: 'rgba(89, 89, 102, 1)',
              minHeight: 32,
              minWidth: 0,
              padding: '0 12px',
              marginLeft: '4px',
              borderRadius: '6px',
              lineHeight: 1,
              marginBottom: 0,
              display: 'inline-flex',
              alignItems: 'center'
            },
            '& .MuiTab-root .MuiTab-wrapper': {
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1
            },
            '& .MuiTab-root.Mui-selected': {
              color: 'rgba(2, 2, 66, 1)',
              backgroundColor: 'rgba(2, 2, 66, 0.12)',
              border: '1px solid rgba(2, 2, 66, 0.18)'
            }
          }}
        >
          {TABS.map((label, idx) => (
            <Tab key={label} label={label} value={idx} />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
};

export default Estimates2DetailPage;


